/**
 * LegacyAiCommandWidget — the pre-Reinsight "AI Assistant" floating command
 * widget, byte-for-byte what production shows today (origin/server
 * components/AiAssistant/index.tsx): typed or spoken natural-language commands
 * POSTed to /ai/command (soft-200 contract — success:false is an in-band
 * error). App.tsx renders THIS while the server flag AI_ASSISTANT_ENABLED is
 * off and Reinsight (./index.tsx) once it is on, so flipping the flag swaps
 * assistants with no deploy and the dark period keeps the exact widget users
 * already have. The `ai-assistant` class stays because the onboarding tours
 * target it. DELETE this file when the assistant launches for good.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Collapse,
  Tooltip,
  useMediaQuery,
  useTheme,
  Link,
  Button,
} from '@mui/material';
import { 
  Send as SendIcon,
  KeyboardArrowDown as MinimizeIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useQueryClient } from 'react-query';
import VoiceCommandButton from '../VoiceCommandButton';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../../offline';

const OFFLINE_HELPER_TEXT =
  'AI Assistant needs an internet connection — it will be back when you reconnect';

interface AiResponse {
  success: boolean;
  message: string;
  data: any;
}

export const LegacyAiCommandWidget: React.FC = () => {
  const [command, setCommand] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
    invoiceData?: { invoiceId: string; invoiceNumber: string };
  }>({
    open: false,
    message: '',
    type: 'success',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Observed connectivity — defaults online when offline mode is off (no provider).
  const { isOnline } = useOffline();

  const processCommand = async (commandText: string) => {
    if (!commandText.trim()) return;

    // AI commands can never work offline — block up front with a neutral
    // notice instead of letting axios surface a raw "Network Error".
    if (!isOnline) {
      setNotification({
        open: true,
        message: OFFLINE_HELPER_TEXT,
        type: 'info',
      });
      return;
    }

    setIsLoading(true);
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.post<AiResponse>('/ai/command', {
        command: commandText,
        context: {
          // Add any relevant context here
        },
        timeZone,
      });

      if (response.data.success === false) {
        setNotification({
          open: true,
          message: response.data.message || 'Sorry, your command could not be executed.',
          type: 'error',
        });
        return;
      }

      // Check if this is an invoice creation response
      let invoiceData = undefined;
      if (response.data.data && 'invoiceNumber' in response.data.data && 'invoiceId' in response.data.data) {
        invoiceData = {
          invoiceId: response.data.data.invoiceId,
          invoiceNumber: response.data.data.invoiceNumber,
        };
      }

      // Show success message
      setNotification({
        open: true,
        message: response.data.message,
        type: 'success',
        invoiceData,
      });

      // Invalidate relevant queries based on the action
      if (response.data.data) {
        if ('date' in response.data.data) {
          queryClient.invalidateQueries('appointments');
        }
        if ('amount' in response.data.data) {
          queryClient.invalidateQueries('invoices');
        }
        if ('breed' in response.data.data) {
          queryClient.invalidateQueries('horses');
        }
      }

      // Clear the command only if it was typed (not from voice)
      setCommand('');
    } catch (error) {
      const err = error as any;
      console.error('AI Assistant Error:', err);
      let message = 'Sorry, your command could not be executed. Please provide more details or check your input.';
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.message) {
        message = err.message;
      }
      setNotification({
        open: true,
        message,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await processCommand(command);
  };

  const handleVoiceCommand = async (transcript: string) => {
    await processCommand(transcript);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Responsive sizing
  const getExpandedWidth = () => {
    if (isMobile) return 'calc(100vw - 24px)'; // Full width minus margins on mobile
    if (isTablet) return '350px'; // Medium size for tablets/iPad
    return '400px'; // Desktop size
  };

  const getCollapsedSize = () => {
    if (isMobile) return 48; // Smaller on mobile
    return 56; // Default size
  };

  const getPosition = () => {
    if (isMobile) return { bottom: 12, right: 12 }; // Closer to edge on mobile
    return { bottom: 20, right: 20 };
  };

  const position = getPosition();
  const collapsedSize = getCollapsedSize();

  return (
    <Box sx={{ 
      position: 'fixed', 
      bottom: position.bottom, 
      right: position.right, 
      width: isExpanded ? getExpandedWidth() : collapsedSize,
      maxWidth: isExpanded ? { xs: 'calc(100vw - 24px)', sm: '350px', md: '400px' } : collapsedSize,
      height: isExpanded ? 'auto' : collapsedSize,
      zIndex: 1000,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      transformOrigin: 'right bottom',
    }}
    className="ai-assistant"
    >
      <Paper
        elevation={3}
        sx={{
          p: isExpanded ? { xs: 1.5, sm: 2 } : 0,
          backgroundColor: isExpanded ? 'background.paper' : '#C9772B',
          borderRadius: isExpanded ? 2 : '50%',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: isExpanded ? 'scale(1)' : 'scale(0.95)',
          border: isExpanded ? '1px solid #D1D5DB' : 'none',
          boxShadow: isExpanded
            ? '0 8px 30px rgba(0,0,0,0.12)'
            : '0 6px 24px rgba(139,94,52,0.45)',
          '&:hover': {
            transform: isExpanded ? 'scale(1)' : 'scale(1.05)',
            boxShadow: isExpanded
              ? '0 8px 30px rgba(0,0,0,0.12)'
              : '0 8px 32px rgba(139,94,52,0.55)',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: isExpanded ? { xs: 0.5, sm: 1 } : 0,
          minHeight: isExpanded ? 'auto' : { xs: '48px', sm: '56px' },
          p: isExpanded ? 0 : { xs: '12px', sm: '16px' },
          position: 'relative',
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              opacity: isExpanded ? 1 : 0,
              visibility: isExpanded ? 'visible' : 'hidden',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isExpanded ? 'translateY(0)' : 'translateY(10px)',
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            AI Assistant
          </Typography>
          <Tooltip title={isExpanded ? "Minimize" : "AI Assistant"}>
            <IconButton 
              onClick={toggleExpanded} 
              size="small"
              sx={{
                position: isExpanded ? 'relative' : 'absolute',
                right: isExpanded ? 0 : '50%',
                top: !isExpanded ? '50%' : 0,
                transform: !isExpanded ? 'translate(50%, -50%) rotate(180deg)' : 'rotate(0deg)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                width: { xs: '36px', sm: '32px' },
                height: { xs: '36px', sm: '32px' },
                minWidth: { xs: 44, sm: 32 }, // Touch target
                minHeight: { xs: 44, sm: 32 }, // Touch target
                color: isExpanded ? 'text.primary' : '#fff',
                '&:hover': {
                  backgroundColor: isExpanded ? 'action.hover' : 'rgba(255,255,255,0.15)',
                  color: isExpanded ? 'text.primary' : '#fff',
                },
              }}
            >
              {isExpanded ? <MinimizeIcon /> : <AiIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <Collapse 
          in={isExpanded} 
          timeout={300}
          easing={{
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box
            sx={{
              opacity: isExpanded ? 1 : 0,
              transform: isExpanded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              visibility: isExpanded ? 'visible' : 'hidden',
            }}
          >
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{
                transform: isExpanded ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.43 },
                display: { xs: 'none', sm: 'block' }, // Hide on very small screens to save space
              }}
            >
              {isOnline
                ? 'Ask me to book appointments, create invoices, or add new patients. Use voice commands or type below.'
                : OFFLINE_HELPER_TEXT}
            </Typography>
            <form onSubmit={handleSubmit}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 1 },
                  transform: isExpanded ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={"Type your command..."}
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  disabled={isLoading || !isOnline}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: { xs: 1, sm: 1.25 },
                      px: { xs: 1.5, sm: 2 },
                    },
                    flexGrow: 1,
                    minWidth: 0, // Allow shrinking
                  }}
                />
                <VoiceCommandButton
                  onVoiceCommand={handleVoiceCommand}
                  isProcessing={isLoading}
                  disabled={!isOnline}
                  size={isMobile ? 'small' : 'medium'}
                />
                <IconButton
                  type="submit"
                  color="primary"
                  disabled={isLoading || !command.trim() || !isOnline}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    minWidth: { xs: 40, sm: 44 },
                    minHeight: { xs: 40, sm: 44 },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={isMobile ? 20 : 24} /> : <SendIcon fontSize={isMobile ? 'small' : 'medium'} />}
                </IconButton>
              </Box>
            </form>
          </Box>
        </Collapse>
      </Paper>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.invoiceData ? 8000 : 4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: '100%' }}
          action={
            notification.invoiceData && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigate('/billing', { state: { invoiceId: notification.invoiceData?.invoiceId } });
                  handleCloseNotification();
                }}
                sx={{ fontWeight: 'bold' }}
              >
                View Invoice
              </Button>
            )
          }
        >
          <Box>
            {notification.message}
            {notification.invoiceData && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Invoice #{notification.invoiceData.invoiceNumber}
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LegacyAiCommandWidget;
